import { forwardRef, type CSSProperties, type ReactNode, type Ref } from 'react';
import { Box, type BoxProps } from '../Box/Box.js';
import { resolveSpacing } from '../../theme/index.js';
import './SimpleGrid.css';

/**
 * Mobile-first responsive column map. Each breakpoint inherits the next
 * smaller one when omitted: `{ base: 1, md: 3 }` renders 1 column below
 * 768px and 3 from 768px upwards. Breakpoints are `min-width` queries:
 * `sm` 640 · `md` 768 · `lg` 1024 · `xl` 1280 (see `SIMPLE_GRID_BREAKPOINTS`).
 */
export interface SimpleGridResponsiveCols {
  base?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

/** `cols` accepts a fixed number (unchanged behaviour) or a responsive map. */
export type SimpleGridCols = number | SimpleGridResponsiveCols;

/** Breakpoints (px, `min-width`) used by the responsive `cols` map: mirrors `SimpleGrid.css`. */
export const SIMPLE_GRID_BREAKPOINTS = Object.freeze({ sm: 640, md: 768, lg: 1024, xl: 1280 });

export interface SimpleGridProps extends BoxProps {
  /** Column count: a number, or a `{ base, sm, md, lg, xl }` map (mobile-first). */
  cols?: SimpleGridCols;
  spacing?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
  verticalSpacing?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'xxxl' | number | string;
}

const BREAKPOINT_KEYS = ['base', 'sm', 'md', 'lg', 'xl'] as const;

/**
 * Emit one `--rr-simple-grid-cols-<bp>` custom property per provided
 * breakpoint. The CSS fallback chain in `SimpleGrid.css` makes each
 * breakpoint inherit the next smaller one, so omitted keys need no var.
 */
function responsiveColsStyle(cols: SimpleGridResponsiveCols): CSSProperties {
  const vars: Record<string, string> = {};
  for (const key of BREAKPOINT_KEYS) {
    const n = cols[key];
    if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
      vars[`--rr-simple-grid-cols-${key}`] = String(Math.floor(n));
    }
  }
  return vars as CSSProperties;
}

/**
 * Equal-width auto-fitting grid.
 *
 * `cols={3}` keeps the original inline `grid-template-columns` (byte-identical
 * output); `cols={{ base: 1, sm: 2, lg: 4 }}` switches to CSS variables +
 * media queries so the column count follows the viewport.
 */
export const SimpleGrid = forwardRef(function SimpleGrid(
  { cols = 1, spacing = 'md', verticalSpacing, style, children, ...rest }: SimpleGridProps,
  ref: Ref<HTMLElement>
): ReactNode {
  const responsiveCols = typeof cols === 'object' && cols !== null ? cols : null;
  return (
    <Box
      ref={ref}
      {...rest}
      className={['rr-simple-grid', responsiveCols && 'rr-simple-grid--responsive', rest.className]
        .filter(Boolean)
        .join(' ')}
      style={{
        ...style,
        ...(responsiveCols
          ? responsiveColsStyle(responsiveCols)
          : { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }),
        columnGap: resolveSpacing(spacing),
        rowGap: resolveSpacing(verticalSpacing ?? spacing)
      }}
    >
      {children}
    </Box>
  );
});
